import { assign, clone } from "lodash";
import { useEffect, useRef } from "react";

interface Colour {
    r: number;
    g: number;
    b: number;
    a?: number;
}

interface ColourSet {
    primary: Colour;
    darker: Colour;
    lighter: Colour;
}

const dspMap: {[key: string]: number} = {
    // Streams
    "spotify": 0,
    "spotify-stream": 0,
    "deezer": 1,
    "deezer-stream": 1,
    "apple-music": 2,
    "am-stream": 2,
    "napster": 3,
    "napster-stream": 3,
    "amazon": 4,
    "amazon-stream": 4,
    "youtube-subscription": 5,
    "youtube-subscription-stream": 5,
    "youtube-ad": 6,
    "youtube-ad-stream": 6,

    // Downloads
    "itunes-track-download": 0,
    "itunes-album-download": 1,
    "amazon-track-download": 2,
    "amazon-album-download": 3,
};

/**
 * The colours for charts and similar.
 * If adding a new one, see src/resources/styles/debug for tools to generate
 */
export const colours: ColourSet[] = [
    {
        primary: { r: 49, g: 217, b: 148 }, // #31D994 - Spring green
        darker:  { r: 33, g: 180, b: 119 },
        lighter: { r: 90, g: 225, b: 169 },
    },
    {
        primary: { r: 219, g: 127, b: 212 }, // #DB7FD4 - Orchid
        darker:  { r: 204, g: 73, b: 194 },
        lighter: { r: 226, g: 153, b: 221 },
    },
    {
        primary: { r: 255, g: 124, b: 124 }, // #FF7C7C - Peach
        darker:  { r: 255, g: 48, b: 48 },
        lighter: { r: 255, g: 150, b: 150 },
    },
    {
        primary: { r: 90, g: 142, b: 244 }, // #5A8EF4 - Cornflower blue
        darker:  { r: 27, g: 99, b: 240 },
        lighter: { r: 123, g: 165, b: 246 },
    },
    {
        primary: { r: 179, g: 145, b: 251 }, // #B391FB - Lilac
        darker:  { r: 126, g: 69, b: 248 },
        lighter: { r: 194, g: 167, b: 252 },
    },
    {
        primary: { r: 209, g: 221, b: 64 }, // #D1DD40 - Lime
        darker:  { r: 181, g: 194, b: 34 },
        lighter: { r: 218, g: 228, b: 102 },
    },
    {
        primary: { r: 236, g: 192, b: 33 }, // #ECC021 - Mustard
        darker:  { r: 198, g: 159, b: 17 },
        lighter: { r: 240, g: 205, b: 77 },
    },
    {
        primary: { r: 255, g: 173, b: 98 }, // #FFAD62 - Sunset
        darker:  { r: 255, g: 136, b: 27 },
        lighter: { r: 255, g: 189, b: 129 },
    },
    {
        primary: { r: 2, g: 180, b: 236 }, // #02B4EC - Wata cyan
        darker:  { r: 2, g: 144, b: 189 },
        lighter: { r: 39, g: 202, b: 253 },
    },
];

export const getColour = (index: number) => clone(colours[index]);
export const getColourPrimary = (index: number) => getColour(index).primary;
export const getColourDarker = (index: number) => getColour(index).darker;
export const getColourLighter = (index: number) => getColour(index).lighter;
export const getColourByDsp = (dsp: string) => getColour(dspMap[dsp] || 0);

export const applyOpacity = (colour: Colour, opacity: number) =>
    assign(clone(colour), { a: opacity }) as Colour;

export const rgbString = (colour: Colour, opacity?: number) => {
    if (opacity != null) {
        colour = applyOpacity(colour, opacity);
    }
    const values = [ colour.r, colour.g, colour.b ];
    if (colour.a != null) {
        values.push(colour.a);
    }
    return "rgb" + (values.length === 4 ? "a" : "") + "(" + values.join(",") + ")";
};

export const rgbStringPrimary = (index: number, opacity?: number) =>
    rgbString(getColourPrimary(index), opacity);
export const rgbStringDarker = (index: number, opacity?: number) =>
    rgbString(getColourDarker(index), opacity);
export const rgbStringLighter = (index: number, opacity?: number) =>
    rgbString(getColourLighter(index), opacity);
export const rgbStringPrimaryByDsp = (dsp: string, opacity?: number) =>
    rgbString(getColourByDsp(dsp).primary, opacity);
export const rgbStringDarkerByDsp = (dsp: string, opacity?: number) =>
    rgbString(getColourByDsp(dsp).darker, opacity);

// Hooks
export const usePrevious = <T>(value: T): T | undefined => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

export const useHasChanged = <T>(val: T) => {
    const prevVal = usePrevious<T>(val);
    return prevVal !== val;
};
